import * as React from "react"
import { Plus, Check } from "lucide-react"
import { useCreateAccount, useUpdateAccount } from "@/features/accounts/hooks/useAccounts"
import type { Account } from "@/types"
import { Button } from "@/shared/ui/atoms/Button"
import { Input } from "@/shared/ui/atoms/Input"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/atoms/Select"

interface AccountFormProps {
  initialAccount?: Account | null
  onSuccess: () => void
  onCancel?: () => void
}

export function AccountForm({ initialAccount, onSuccess, onCancel }: AccountFormProps) {
  const isEditing = Boolean(initialAccount)
  const { mutate: createAccount, isPending: isCreating } = useCreateAccount()
  const { mutate: updateAccount, isPending: isUpdating } = useUpdateAccount()
  const isPending = isCreating || isUpdating

  const [name, setName] = React.useState(initialAccount?.name || "")
  const [currency, setCurrency] = React.useState<Account["currency"]>(initialAccount?.currency || "HNL")
  const [startingBalance, setStartingBalance] = React.useState(
    initialAccount?.current_balance ? String(initialAccount.current_balance) : "0"
  )
  const [isSavings, setIsSavings] = React.useState(initialAccount?.is_savings_account || false)
  const [isActive, setIsActive] = React.useState(
    initialAccount?.is_active !== undefined ? initialAccount.is_active : true
  )

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!name.trim()) return

    if (isEditing && initialAccount) {
      updateAccount(
        {
          id: initialAccount.id,
          name: name.trim(),
          currency,
          is_savings_account: isSavings,
          is_active: isActive,
        },
        { onSuccess }
      )
    } else {
      createAccount(
        {
          name: name.trim(),
          currency,
          current_balance: Number(startingBalance) || 0,
          is_savings_account: isSavings,
        },
        { onSuccess }
      )
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <label className="flex flex-col gap-2 text-sm font-medium">
        Nombre de la cuenta
        <Input
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Ej. Cuenta principal"
          required
        />
      </label>

      <div className={isEditing ? "grid gap-4 grid-cols-1" : "grid gap-4 sm:grid-cols-2"}>
        <label className="flex flex-col gap-2 text-sm font-medium">
          Moneda
          <Select value={currency} onValueChange={(value) => setCurrency(value as Account["currency"])}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="HNL">Lempiras (HNL)</SelectItem>
                <SelectItem value="USD">Dólares (USD)</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </label>

        {!isEditing && (
          <label className="flex flex-col gap-2 text-sm font-medium">
            Saldo inicial
            <Input
              type="number"
              min="0"
              step="0.01"
              value={startingBalance}
              onChange={(event) => setStartingBalance(event.target.value)}
            />
          </label>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <label className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-3 text-sm">
          <input
            type="checkbox"
            checked={isSavings}
            onChange={(event) => setIsSavings(event.target.checked)}
            className="size-4 accent-primary"
          />
          Esta cuenta se usa exclusivamente para ahorros
        </label>

        {isEditing && (
          <label className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-3 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
              className="size-4 accent-primary"
            />
            <span>
              Cuenta activa <span className="text-xs text-muted-foreground">(Las cuentas inactivas quedan archivadas sin perder movimientos)</span>
            </span>
          </label>
        )}
      </div>

      <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 pt-2">
        {onCancel ? (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
        ) : null}
        <Button type="submit" disabled={isPending} className="w-full sm:w-auto min-w-[130px]">
          {isEditing ? (
            <>
              <Check data-icon="inline-start" />
              {isPending ? "Guardando…" : "Actualizar cuenta"}
            </>
          ) : (
            <>
              <Plus data-icon="inline-start" />
              {isPending ? "Creando…" : "Crear cuenta"}
            </>
          )}
        </Button>
      </div>
    </form>
  )
}

